/* eslint-disable max-len */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataEntryMode } from '../../types/DataEntryMode';
import { NonPublicStudentEntryModel } from '../../types/NonPublicStudentEntryModel';
import { NonPublicStudentFormViewModel } from '../../types/NonPublicStudentFormViewModel';
import { NonPublicStudentListItemModel } from '../../types/NonPublicStudentListItemModel';
import { NonPublicStudentScheduleFormViewModel } from '../../types/NonPublicStudentScheduleFormViewModel';
import { NonPublicStudentScheduleListItemModel } from '../../types/NonPublicStudentScheduleListItemModel';
import { NonPublicStudentScheduleModel } from '../../types/NonPublicStudentScheduleModel';
import { isEmptyObject } from '../../utilities/helperUtilities';
import type { RootState } from '../store';
import {
  fetchNonPublicStudentById,
  fetchNonPublicStudentFormViewModel,
  fetchNonPublicStudentListItems,
  fetchNonPublicStudentScheduleByNpsId,
  fetchNonPublicStudentScheduleByScheduleId,
  fetchNonPublicStudentScheduleFormViewModel,
  fetchNpsSchoolYears,
} from '../thunks/nonPublicStudentThunks';
import { ReactSelectOption } from '../../types/ReactSelectOption';

interface NonPublicStudentState {
  schoolYear: string;
  loadingListItems: boolean;
  listItems: NonPublicStudentListItemModel[];

  loadingNonPublicStudent: boolean;
  nonPublicStudent: NonPublicStudentEntryModel;
  scheduleListItems: NonPublicStudentScheduleListItemModel[];
  nonPublicStudentSchedule: NonPublicStudentScheduleModel;

  loadingFormViewModel: boolean;
  formViewModel: NonPublicStudentFormViewModel;
  scheduleFormViewModel: NonPublicStudentScheduleFormViewModel;

  dataEntryMode: DataEntryMode;
  hasSearchMatch: boolean;
  has200500SearchMatch: boolean;

  allowEdit: boolean;
}

export const defaultNonPublicStudentState: NonPublicStudentEntryModel = {
  id: '',
  district: '',
  schoolYear: '',
  schoolType: '',
  schoolTypeInitial: '',
  schoolId: '',
  school: '',
  schoolName: '',
  studentIdentifier: '',
  lastOrSurname: '',
  firstName: '',
  middleName: '',
  birthDate: '',
  personIdentifierSsn: '',
  sex: '',
  hispanicOrLatinoEthnicity: '',
  race: '',
  specialEducation: '',
  englishLearnerStatus: '',
  singleParentOrSinglePregnantWomanStatus: '',
  cteaeDisplacedHomemakerIndicator: '',
  perkinsPostProgramPlacementIndicator: '',
  enrollmentEntryDate: '',
  entryType: '',
  enrollmentStatus: '',
  gradeLevel: '',
  eligibilityDeterminationDate: '',
  iepStartDate: '',
  projectedHSDiplomaType: '',
  programParticipationExitDate: '',
  primaryDisabilityType: '',
  secondaryDisabilityType: '',
  significantCognitiveDisabilityIndicator: '',
  ideaEducationalEnvironment: '',
  relatedServices: [],
  outcomeAEntryScore: '',
  outcomeAEntryScoreDate: '',
  outcomeAExitScore: '',
  outcomeAExitScoreDate: '',
  outcomeAProgress: '',
  outcomeBEntryScore: '',
  outcomeBEntryScoreDate: '',
  outcomeBExitScore: '',
  outcomeBExitScoreDate: '',
  outcomeBProgress: '',
  outcomeCEntryScore: '',
  outcomeCEntryScoreDate: '',
  outcomeCExitScore: '',
  outcomeCExitScoreDate: '',
  outcomeCProgress: '',
  hasMatch: false,
  ageEvaluationYear: '',
};

export const defaultDataEntryMode = DataEntryMode.NEW;

export const defaultStudentSchedule: NonPublicStudentScheduleModel = {
  id: '',
  npsId: '',
  schoolIdentifier: '',
  schoolName: '',
  districtIdentifier: '',
  districtName: '',
  studentIdentifier: '',
  schoolYear: '',
  staffMemberIdentifier: '',
  staffMemberName: '',
  courseIdentifier: '',
  courseTitle: '',
  courseSectionIdentifier: '',
  courseSectionDescription: '',
  status: '',
};

export const defaultScheduleFormViewModel: NonPublicStudentScheduleFormViewModel =
  {
    defaultSchoolYear: '',
    extendedCourseCodeOptions: [],
    statusOptions: [],
  };

export const defaultFormState: NonPublicStudentFormViewModel = {
  defaultSchoolYear: '',
  defaultSchoolYearOption: { value: '', label: '' },
  selectedSchoolYearOption: { value: '', label: '' },
  // ageEvaluationYear: '',
  districts: [],
  ideaEducationalEnvironments: [],
  schoolTypeOptions: [],
  sexOptions: [],
  hispanicOrLatinoEthnicityOptions: [],
  raceOptions: [],
  specialEducationOptions: [],
  englishLearnerStatusOptions: [],
  enrollmentStatusOptions: [],
  gradeLevelOptions: [],
  singleParentOrSinglePregnantWomanStatusOptions: [],
  cteaeDisplacedHomemakerIndicatorOptions: [],
  perkinsPostProgramPlacementIndicatorOptions: [],
  significantCognitiveDisabilityIndicatorOptions: [],
  primaryDisabilityTypeOptions: [],
  secondaryDisabilityTypeOptions: [],
  relatedServicesOptions: [],
  projectedHSDiplomaTypeOptions: [],
  outcomeExitScoreOptions: [],
  outcomeEntryScoreOptions: [],
  outcomeProgressOptions: [],
  schoolYearOptions: [],
};

export const initialState: NonPublicStudentState = {
  schoolYear: '',
  loadingListItems: false,
  listItems: [],
  loadingNonPublicStudent: false,
  nonPublicStudent: defaultNonPublicStudentState,
  nonPublicStudentSchedule: defaultStudentSchedule,
  scheduleListItems: [],
  loadingFormViewModel: false,
  formViewModel: defaultFormState,
  scheduleFormViewModel: defaultScheduleFormViewModel,
  dataEntryMode: defaultDataEntryMode,
  hasSearchMatch: false,
  has200500SearchMatch: false,
  allowEdit: false,
};

export const nonPublicStudentSlice = createSlice({
  name: 'nonPublicStudent',
  initialState,
  reducers: {
    setSchoolYearFilter: (state, action: PayloadAction<string>) => {
      state.schoolYear = action.payload;
    },
    clearNonPublicStudent: (state) => {
      state.nonPublicStudent = defaultNonPublicStudentState;
    },
    saveNonPublicStudent: (
      state,
      action: PayloadAction<NonPublicStudentEntryModel>
    ) => {
      state.nonPublicStudent = action.payload;
    },
    clearNonPublicStudentSchedule: (state) => {
      state.nonPublicStudentSchedule = defaultStudentSchedule;
    },
    saveNonPublicStudentSchedule: (
      state,
      action: PayloadAction<NonPublicStudentScheduleModel>
    ) => {
      state.nonPublicStudentSchedule = action.payload;
    },
    setNonPublicStudentDataEntryMode: (
      state,
      action: PayloadAction<DataEntryMode>
    ) => {
      state.dataEntryMode = action.payload;
    },
    setHasSearchMatch: (state, action: PayloadAction<boolean>) => {
      state.hasSearchMatch = action.payload;
    },
    setHas200500SearchMatch: (state, action: PayloadAction<boolean>) => {
      state.has200500SearchMatch = action.payload;
    },
    setNpsSchoolYear: (
      state,
      action: PayloadAction<ReactSelectOption>
    ) => {
      state.formViewModel.selectedSchoolYearOption = action.payload;
      state.formViewModel.defaultSchoolYearOption = action.payload;
      state.schoolYear = action.payload.label;
      state.scheduleFormViewModel = defaultScheduleFormViewModel;
    },
    initializeSchoolYear: (state) => {
      const options = state.formViewModel.schoolYearOptions;
      
      if (options && options.length > 0) {
        let latestOption = options[0];
        for (const option of options) {
          const currentEndYear = parseInt(option.text.split('-')[1] || option.text);
          const latestEndYear = parseInt(latestOption.text.split('-')[1] || latestOption.text);
          
          if (currentEndYear > latestEndYear) {
            latestOption = option;
          }
        }
        
        if (latestOption) {
          const previousSchoolYear = state.schoolYear;
          state.schoolYear = latestOption.text;
          
          state.formViewModel.selectedSchoolYearOption = {
            value: latestOption.value,
            label: latestOption.text,
          };
          state.formViewModel.defaultSchoolYearOption = {
            value: latestOption.value,
            label: latestOption.text,
          };
          
          if (previousSchoolYear !== latestOption.text) {
            state.scheduleFormViewModel = defaultScheduleFormViewModel;
          }
        }
      }
    },
    setAllowEdit: (state, action: PayloadAction<boolean>) => {
      state.allowEdit = action.payload;
    },
  },
  extraReducers: (builder) => {
    // fetchNonPublicStudentList
    builder.addCase(fetchNonPublicStudentListItems.pending, (state) => {
      state.listItems = [];
      state.loadingListItems = true;
    });
    builder.addCase(
      fetchNonPublicStudentListItems.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.listItems = action.payload;
        }
        state.loadingListItems = false;
      }
    );
    builder.addCase(
      fetchNonPublicStudentListItems.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingListItems = false;
        }
      }
    );

    // fetchNonPublicStudentById
    builder.addCase(fetchNonPublicStudentById.pending, (state) => {
      state.nonPublicStudent = defaultNonPublicStudentState;
      state.loadingNonPublicStudent = true;
    });
    builder.addCase(fetchNonPublicStudentById.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.nonPublicStudent = action.payload;
      }
      state.loadingNonPublicStudent = false;
    });
    builder.addCase(fetchNonPublicStudentById.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingNonPublicStudent = false;
      }
    });

    // fetchNonPublicStudentScheduleByNpsId
    builder.addCase(fetchNonPublicStudentScheduleByNpsId.pending, (state) => {
      state.scheduleListItems = [];
      state.loadingNonPublicStudent = true;
    });
    builder.addCase(
      fetchNonPublicStudentScheduleByNpsId.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.scheduleListItems = action.payload;
        }
        state.loadingNonPublicStudent = false;
      }
    );
    builder.addCase(
      fetchNonPublicStudentScheduleByNpsId.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingNonPublicStudent = false;
        }
      }
    );

    // fetchNonPublicStudentScheduleByScheduleId
    builder.addCase(
      fetchNonPublicStudentScheduleByScheduleId.pending,
      (state) => {
        state.nonPublicStudentSchedule = defaultStudentSchedule;
        state.loadingNonPublicStudent = true;
      }
    );
    builder.addCase(
      fetchNonPublicStudentScheduleByScheduleId.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.nonPublicStudentSchedule = action.payload;
        }
        state.loadingNonPublicStudent = false;
      }
    );
    builder.addCase(
      fetchNonPublicStudentScheduleByScheduleId.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingNonPublicStudent = false;
        }
      }
    );

    //fetchNonPublicStudentFormViewModel
    builder.addCase(fetchNonPublicStudentFormViewModel.pending, (state) => {
      state.formViewModel = defaultFormState;
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchNonPublicStudentFormViewModel.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.formViewModel = action.payload;
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchNonPublicStudentFormViewModel.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    //fetchNonPublicStudentScheduleFormViewModel
    builder.addCase(
      fetchNonPublicStudentScheduleFormViewModel.pending,
      (state) => {
        state.scheduleFormViewModel = defaultScheduleFormViewModel;
        state.loadingFormViewModel = true;
      }
    );
    builder.addCase(
      fetchNonPublicStudentScheduleFormViewModel.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.scheduleFormViewModel = action.payload;
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchNonPublicStudentScheduleFormViewModel.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    //fetch School Year options
    builder.addCase(fetchNpsSchoolYears.pending, (state) => {
      state.formViewModel.schoolYearOptions = [];
      state.loadingFormViewModel = true;
    });
    builder.addCase(fetchNpsSchoolYears.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.formViewModel.schoolYearOptions = action.payload;
        
        let latestOption = action.payload[0];
        
        for (const option of action.payload) {
          const currentEndYear = parseInt(option.text.split('-')[1] || option.text);
          const latestEndYear = parseInt(latestOption.text.split('-')[1] || latestOption.text);
          
          if (currentEndYear > latestEndYear) {
            latestOption = option;
          }
        }
        
        const defaultValue = latestOption || {
          value: '',
          label: '',
        };

        state.formViewModel.defaultSchoolYearOption = {
          value: defaultValue.value,
          label: defaultValue.text,
        };
        
        if (state.schoolYear && state.schoolYear !== '') {
          const userSelectedOption = action.payload.find(option => option.text === state.schoolYear);
          if (userSelectedOption) {
            state.formViewModel.selectedSchoolYearOption = {
              value: userSelectedOption.value,
              label: userSelectedOption.text,
            };
          }
        } else {
          state.formViewModel.selectedSchoolYearOption = {
            value: defaultValue.value,
            label: defaultValue.text,
          };
          state.schoolYear = defaultValue.text;
        }
      }
      state.loadingFormViewModel = false;
    });
    builder.addCase(fetchNpsSchoolYears.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingFormViewModel = false;
      }
    });
  },
});

export const { setSchoolYearFilter } = nonPublicStudentSlice.actions;

export const {
  clearNonPublicStudent,
  clearNonPublicStudentSchedule,
  saveNonPublicStudent,
  saveNonPublicStudentSchedule,
  setNonPublicStudentDataEntryMode,
  setHasSearchMatch,
  setHas200500SearchMatch,
  setNpsSchoolYear,
  initializeSchoolYear,
  setAllowEdit,
  // setHasNon200500SearchMatchState,
  // setHasSearchMatchState,
} = nonPublicStudentSlice.actions;

export const selectSchoolYear = (state: RootState): string =>
  state.nonPublicStudent.schoolYear;
// listItems
export const selectNonPublicStudentLoadingListItems = (
  state: RootState
): boolean => state.nonPublicStudent.loadingListItems;
export const selectNonPublicStudentListItems = (
  state: RootState
): NonPublicStudentListItemModel[] => state.nonPublicStudent.listItems;

// NonPublicStudent
export const selectNonPublicStudentLoadingNonPublicStudent = (
  state: RootState
): boolean => state.nonPublicStudent.loadingNonPublicStudent;
export const selectNonPublicStudentNonPublicStudent = (
  state: RootState
): NonPublicStudentEntryModel => state.nonPublicStudent.nonPublicStudent;
export const selectNonPublicStudentScheduleListItems = (
  state: RootState
): NonPublicStudentScheduleListItemModel[] =>
  state.nonPublicStudent.scheduleListItems;
export const selectNonPublicStudentScheduleModel = (
  state: RootState
): NonPublicStudentScheduleModel =>
  state.nonPublicStudent.nonPublicStudentSchedule;

// formViewModel
export const selectNonPublicStudentLoadingFormViewModel = (
  state: RootState
): boolean => state.nonPublicStudent.loadingFormViewModel;
export const selectNonPublicStudentFormViewModel = (
  state: RootState
): NonPublicStudentFormViewModel => state.nonPublicStudent.formViewModel;
export const selectNonPublicStudentScheduleFormViewModel = (
  state: RootState
): NonPublicStudentScheduleFormViewModel =>
  state.nonPublicStudent.scheduleFormViewModel;

// dataEntryMode
export const selectNonPublicStudentDataEntryMode = (
  state: RootState
): DataEntryMode => state.nonPublicStudent.dataEntryMode;

export const selectHasSearchMatch = (
  state: RootState
): boolean => state.nonPublicStudent.hasSearchMatch;
export const selectHas200500SearchMatch = (
  state: RootState
): boolean => state.nonPublicStudent.has200500SearchMatch;

export const selectAllowEdit = (
  state: RootState
): boolean => state.nonPublicStudent.allowEdit;

export default nonPublicStudentSlice.reducer;
